export type SubtitleSlot = 'mainSubtitle' | 'secondSubtitle';

export type BaselineSettings = {
  subtitleSeparation: boolean;
  subY: number;
  mainSubY: number;
  secondSubY: number;
};

export type ViewportRect = {
  width: number;
  height: number;
};

export type BaselineLayout = {
  top: number;
  left: number;
  right: number;
  center: number;
  viewHeight: number;
  viewWidth: number;
  spaceWidth: number;
  /** Top edge of the letterboxed 16:9 content (overlay coords). */
  viewTop: number;
  /** Bottom edge of the letterboxed 16:9 content (overlay coords). */
  viewBottom: number;
};

export type SubYStorageKey = 'subY' | 'mainSubY' | 'secondSubY';

const FIX_RATIO = 16 / 9;
/** Side inset inside the letterboxed content (watch-mode text margins). */
export const HORIZONTAL_BASELINE_RATIO = 0.08;

/** Popup range bounds for subY / mainSubY / secondSubY. */
export const SUB_Y_MIN = 0;
export const SUB_Y_MAX = 1;
/** Popup `<input type="range" step="0.01">` for subY controls. */
export const SUB_Y_STEP = 0.01;

/** Pure letterbox baseline math — mirrors utils.js computeBaseline (B-STYLE-03). */
export function computeBaseline(
  rect: ViewportRect | null | undefined,
  type: SubtitleSlot,
  settings: BaselineSettings,
): BaselineLayout {
  if (!rect) {
    return {
      top: 0,
      left: 0,
      right: 0,
      center: 0,
      viewHeight: 0,
      viewWidth: 0,
      spaceWidth: 0,
      viewTop: 0,
      viewBottom: 0,
    };
  }

  const { width: innerWidth, height: innerHeight } = rect;
  const currentRatio = innerWidth / innerHeight;
  const center = innerHeight / 2;

  const subY = !settings.subtitleSeparation
    ? settings.subY
    : (type === 'mainSubtitle' ? settings.mainSubY : settings.secondSubY);

  let viewWidth: number;
  let spaceWidth: number;
  let viewHeight: number;
  let viewTop: number;
  let viewBottom: number;
  let top: number;
  let left: number;
  let right: number;

  if (currentRatio > FIX_RATIO) {
    viewWidth = innerHeight * FIX_RATIO;
    spaceWidth = (innerWidth - viewWidth) / 2;
    viewHeight = innerHeight;
    viewTop = 0;
    viewBottom = viewHeight;
    top = viewHeight * subY;
    left = spaceWidth + (viewWidth * HORIZONTAL_BASELINE_RATIO);
    right = spaceWidth + (viewWidth * HORIZONTAL_BASELINE_RATIO);
  } else {
    viewWidth = innerWidth;
    spaceWidth = 0;
    viewHeight = innerWidth / FIX_RATIO;
    const spaceHeight = (innerHeight - viewHeight) / 2;
    viewTop = spaceHeight;
    viewBottom = spaceHeight + viewHeight;

    top = (spaceHeight + viewHeight + spaceHeight) * subY;
    left = viewWidth * HORIZONTAL_BASELINE_RATIO;
    right = viewWidth * HORIZONTAL_BASELINE_RATIO;
  }

  return {
    top, left, right, center, viewHeight, viewWidth, spaceWidth, viewTop, viewBottom,
  };
}

/**
 * Treat the measured playback box as the content (no 16:9 letterbox).
 * YouTube: media element client box already matches the picture at any aspect.
 */
export function baselineFromPlaybackBox(
  rect: ViewportRect,
  type: SubtitleSlot,
  settings: BaselineSettings,
): BaselineLayout {
  const width = Math.max(0, rect.width);
  const height = Math.max(0, rect.height);
  const subY = !settings.subtitleSeparation
    ? settings.subY
    : (type === 'mainSubtitle' ? settings.mainSubY : settings.secondSubY);
  return {
    top: height * subY,
    left: width * HORIZONTAL_BASELINE_RATIO,
    right: width * HORIZONTAL_BASELINE_RATIO,
    center: height / 2,
    viewHeight: height,
    viewWidth: width,
    spaceWidth: 0,
    viewTop: 0,
    viewBottom: height,
  };
}

/**
 * Shadowing baseline from a playback measurement.
 * - Netflix (default): {@link computeBaseline} — inscribed 16:9 inside a wider frame.
 * - YouTube: {@link baselineFromPlaybackBox} — video element edges are the picture.
 */
export function computeShadowingBaseline(
  rect: ViewportRect,
  type: SubtitleSlot,
  settings: BaselineSettings,
  opts?: { flushToPlaybackBox?: boolean },
): BaselineLayout {
  if (opts?.flushToPlaybackBox) {
    return baselineFromPlaybackBox(rect, type, settings);
  }
  return computeBaseline(rect, type, settings);
}

export type UnifiedTextSlotTops = {
  mainTop?: number;
  secondTop?: number;
};

/**
 * Unclamped tops for shared-subY (non-separated) text overlays.
 *
 * Height semantics:
 * - `null` = track not armed (not in the layout group) → solo-center the other slot
 * - `0` = track armed but no visible cue content → keep dual absolute tops
 * - `>0` = measured cue stack height
 *
 * Dual (both non-null): main above baseline, second below — independent of either height
 * being zero so one cue ending early does not recenter the other.
 */
export function computeUnifiedTextSlotTops(opts: {
  baselineTop: number;
  spacing: number;
  mainHeight: number | null;
  secondHeight: number | null;
}): UnifiedTextSlotTops {
  const { baselineTop, spacing, mainHeight, secondHeight } = opts;
  if (mainHeight !== null && secondHeight !== null) {
    return {
      mainTop: baselineTop - mainHeight - spacing,
      secondTop: baselineTop + spacing,
    };
  }
  if (mainHeight !== null) {
    return { mainTop: baselineTop - mainHeight / 2 };
  }
  if (secondHeight !== null) {
    return { secondTop: baselineTop - secondHeight / 2 };
  }
  return {};
}

/**
 * Lowest Y the subtitle group bottom may touch: letterboxed 16:9 view bottom.
 * Do not inset for the player control bar — drag/position must reach the content edge;
 * click-through uses `unisubs-sub-pass-through` instead.
 */
export function subtitleContentFloor(viewBottom: number): number {
  return viewBottom;
}

/**
 * When the unified group would extend past `floorY`, shift every member up by
 * the same overflow so the group bottom sits on the floor.
 *
 * Live overlays should call {@link clampUnifiedTextSlotTopsToFloor}, which uses
 * this only when **both** slots have visible content (height &gt; 0).
 */
export function clampSubtitleGroupToViewBottom(
  tops: UnifiedTextSlotTops,
  heights: { mainHeight: number | null; secondHeight: number | null },
  floorY: number,
): UnifiedTextSlotTops {
  let groupBottom = Number.NEGATIVE_INFINITY;
  if (
    tops.mainTop !== undefined
    && heights.mainHeight !== null
    && heights.mainHeight > 0
  ) {
    groupBottom = Math.max(groupBottom, tops.mainTop + heights.mainHeight);
  }
  if (
    tops.secondTop !== undefined
    && heights.secondHeight !== null
    && heights.secondHeight > 0
  ) {
    groupBottom = Math.max(groupBottom, tops.secondTop + heights.secondHeight);
  }
  if (!Number.isFinite(groupBottom)) return tops;

  const overflow = groupBottom - floorY;
  if (!(overflow > 0)) return tops;

  return {
    mainTop: tops.mainTop !== undefined ? tops.mainTop - overflow : undefined,
    secondTop: tops.secondTop !== undefined ? tops.secondTop - overflow : undefined,
  };
}

/**
 * Floor-clamp shared-subY tops without dual-track overlap or empty-partner jumps:
 * - Both heights &gt; 0 → group shift (same overflow on both; stacks stay stacked)
 * - Otherwise → per-slot {@link clampTopToFloor} (armed-empty height 0 does not
 *   couple / release the other track toward center)
 */
export function clampUnifiedTextSlotTopsToFloor(
  tops: UnifiedTextSlotTops,
  heights: { mainHeight: number | null; secondHeight: number | null },
  floorY: number,
): UnifiedTextSlotTops {
  const mainH = heights.mainHeight;
  const secondH = heights.secondHeight;
  const bothVisible =
    mainH !== null && mainH > 0 && secondH !== null && secondH > 0;

  if (bothVisible) {
    return clampSubtitleGroupToViewBottom(tops, heights, floorY);
  }

  return {
    mainTop:
      tops.mainTop !== undefined
        ? clampTopToFloor(tops.mainTop, mainH ?? 0, floorY)
        : undefined,
    secondTop:
      tops.secondTop !== undefined
        ? clampTopToFloor(tops.secondTop, secondH ?? 0, floorY)
        : undefined,
  };
}

/** Clamp a single absolute top so top+height does not pass floorY. */
export function clampTopToFloor(top: number, height: number, floorY: number): number {
  if (!(height > 0)) return top;
  const overflow = top + height - floorY;
  return overflow > 0 ? top - overflow : top;
}

/** Which storage key a drag on main/second should write (matches popup separation UI). */
export function resolveSubYKey(
  slot: 'main' | 'second',
  subtitleSeparation: boolean,
): SubYStorageKey {
  if (!subtitleSeparation) return 'subY';
  return slot === 'main' ? 'mainSubY' : 'secondSubY';
}

export function clampSubY(value: number): number {
  if (!Number.isFinite(value)) return SUB_Y_MIN;
  return Math.min(SUB_Y_MAX, Math.max(SUB_Y_MIN, value));
}

/** Snap to popup range step (0.05). */
export function snapSubY(value: number, step: number = SUB_Y_STEP): number {
  const safeStep = step > 0 ? step : SUB_Y_STEP;
  const snapped = Math.round(clampSubY(value) / safeStep) * safeStep;
  return clampSubY(Number(snapped.toFixed(2)));
}

/**
 * Map a vertical pixel delta to a new subY.
 * Matches computeBaseline: top scales with viewport height in both letterbox modes.
 */
export function nudgeSubYByDelta(
  currentSubY: number,
  deltaPixels: number,
  rect: ViewportRect | null | undefined,
): number {
  if (!rect || !(rect.height > 0)) return clampSubY(currentSubY);
  return clampSubY(currentSubY + deltaPixels / rect.height);
}
